import React from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

export const Hero = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-64 md:w-96 h-64 md:h-96 bg-[#5B42FF]/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-64 md:w-96 h-64 md:h-96 bg-[#5B42FF]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]"></div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-24 md:py-32 w-full">
        {/* Main Content with Artwork */}
        <div className="grid lg:grid-cols-2 gap-0 items-center min-h-[70vh]">
          {/* Text Content */}
          <div className="text-center lg:text-left z-10 pr-0 lg:pr-8">
            {/* Badge */}
            <div className="flex justify-center lg:justify-start mb-6 md:mb-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
              <div className="inline-flex items-center space-x-2 px-3 md:px-4 py-1.5 md:py-2 bg-[#5B42FF]/10 border border-[#5B42FF]/30 rounded-full">
                <Sparkles className="w-3 h-3 md:w-4 md:h-4 text-[#5B42FF]" />
                <span className="text-xs md:text-sm text-white/90">Built in Saudi Arabia. Designed to change the world.</span>
              </div>
            </div>

            {/* Main Heading */}
            <h1 className="text-3xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 md:mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700" style={{ animationDelay: '100ms' }}>
              <span className="block mb-2">Driving Impact Through</span>
              <span className="block text-[#5B42FF]">Revolutionary Technologies</span>
            </h1>

            {/* Subtitle */}
            <p className="text-base md:text-lg lg:text-xl text-white/70 mb-8 md:mb-12 max-w-xl mx-auto lg:mx-0 animate-in fade-in slide-in-from-bottom-4 duration-700" style={{ animationDelay: '200ms' }}>
              We design and deliver advanced digital solutions that empower organizations to shape the future.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4 animate-in fade-in slide-in-from-bottom-4 duration-700" style={{ animationDelay: '300ms' }}>
              <Button
                onClick={() => scrollToSection('services')}
                size="lg"
                className="w-full sm:w-auto bg-[#5B42FF] hover:bg-[#4a35db] text-white px-6 md:px-8 py-5 md:py-6 text-base md:text-lg transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-[#5B42FF]/50 group">
                Explore Our Solutions
                <ArrowRight className="ml-2 w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Button>
              <Button
                onClick={() => scrollToSection('contact')}
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-2 border-white/20 bg-transparent hover:bg-white/5 text-white px-6 md:px-8 py-5 md:py-6 text-base md:text-lg transition-all duration-300 hover:scale-105 hover:border-[#5B42FF]/50">
                Contact Us
              </Button>
            </div>
          </div>

          {/* Artwork - Large and Prominent with Transparent Background */}
          <div className="relative hidden lg:flex items-center justify-center animate-in fade-in slide-in-from-right-4 duration-700" style={{ animationDelay: '200ms' }}>
            <div className="relative w-full h-full flex items-center justify-center">
              {/* Enhanced Glow Effects Behind Artwork */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-[500px] h-[500px] bg-[#5B42FF]/30 rounded-full blur-3xl animate-pulse"></div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-[400px] h-[400px] bg-[#5B42FF]/20 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '0.5s' }}></div>
              </div>
              
              {/* Main Artwork with Transparent Background */}
              <img
                src="https://customer-assets.emergentagent.com/job_revotech-preview/artifacts/fib0ef3b_Source%20file.png"
                alt="RevoTech Innovation"
                className="relative z-10 w-full h-auto max-w-none object-contain drop-shadow-2xl !pt-[30px] !pb-[100px] !pl-[100px] !pr-[30px] !rounded-t-none !rounded-b-none !rounded-l-none !rounded-r-none !opacity-[90%]"
                style={{
                  transform: 'scale(1.5)',
                  transformOrigin: 'center',
                  filter: 'brightness(0.85) drop-shadow(0 0 40px rgba(91, 66, 255, 0.6)) drop-shadow(0 0 80px rgba(91, 66, 255, 0.3))'
                }} />

            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce hidden md:block">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-[#5B42FF] rounded-full animate-pulse"></div>
        </div>
      </div>
    </section>);

};
